<!-- src/components/Layout/HeaderComponent.vue -->
<template>
  <header class="site-header" :class="{ 'top-[42px]': userStore.isImpersonating }">
    <!-- Ever-present return-to-mobile pill for phone users on the desktop site -->
    <button
      v-if="showBackToMobile"
      class="fixed left-1/2 z-modal flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-brand-blue px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-transform active:scale-95"
      style="bottom: max(1rem, env(safe-area-inset-bottom))"
      @click="backToMobile"
    >
      <Smartphone class="icon-sm" /> Back to mobile
    </button>
    <div
      class="mx-auto grid max-w-screen-2xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 px-4 sm:gap-x-6 sm:px-6 xl:grid-cols-[auto_minmax(0,1fr)_auto_auto] xl:px-8"
    >
      <!-- Brand, search and account controls share the first row at every width. -->
      <div class="col-start-1 row-start-1 flex h-16 items-center">
        <!-- WhiteBrown variant — white sun + brown wordmark — required on
             the brand-yellow site-header background per DESIGN_RULES §2.3.
             The primary logo's yellow sun disappears against yellow. -->
        <img
          src="/header_logo_whitebrown.png"
          alt="Grateful Sun Energy Logo"
          class="hidden h-16 w-auto object-contain xl:block"
        />
        <img
          src="/header_logo_sun.png"
          alt="Grateful Sun Energy"
          class="h-12 w-12 object-contain xl:hidden"
        />
      </div>

      <div class="col-start-2 row-start-1 flex min-w-0 justify-center">
        <CustomerSearchAutocomplete include-finished />
      </div>

      <!-- Keep one navigation menu; move it below search before the first row gets crowded. -->
      <nav
        aria-label="Main navigation"
        class="col-span-3 col-start-1 row-start-2 flex h-10 items-center justify-center gap-6 whitespace-nowrap text-sm font-medium text-gray-600 xl:col-span-1 xl:col-start-3 xl:row-start-1 xl:h-16"
      >
        <!-- Dashboard Dropdown -->
        <div ref="dashboardMenuContainer" class="relative" @keydown.esc="closeDashboardMenu">
          <button
            ref="dashboardMenuButton"
            type="button"
            :aria-expanded="dashboardMenuOpen"
            aria-controls="dashboard-menu"
            @click="toggleDashboardMenu"
            class="uppercase transition-colors hover:text-blue-600"
            :class="{ 'font-semibold text-blue-600': isActiveDashboardRoute || dashboardMenuOpen }"
          >
            DASHBOARD
          </button>

          <div
            v-if="dashboardMenuOpen"
            id="dashboard-menu"
            class="absolute left-0 z-dropdown mt-2 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            <RouterLink
              v-for="item in dashboardMenuItems"
              :key="item.path"
              :to="item.path"
              class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 transition hover:bg-blue-50 hover:text-blue-700"
              :class="{ 'bg-blue-50 font-medium text-blue-700': isActive(item.path) }"
              @click="dashboardMenuOpen = false"
            >
              <component :is="item.icon" class="h-4 w-4" />
              {{ item.label }}
            </RouterLink>
          </div>
        </div>

        <div
          v-if="createMenuItems.length"
          ref="createMenuContainer"
          class="relative"
          @keydown.esc="closeCreateMenu"
        >
          <button
            ref="createMenuButton"
            type="button"
            class="uppercase transition-colors hover:text-blue-600"
            :class="{
              'font-semibold text-blue-600':
                createMenuOpen || createMenuItems.some(item => isActive(item.path)),
            }"
            :aria-expanded="createMenuOpen"
            aria-controls="create-menu"
            @click="toggleCreateMenu"
          >
            CREATE
          </button>
          <div
            v-if="createMenuOpen"
            id="create-menu"
            class="absolute left-0 z-dropdown mt-2 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            <RouterLink
              v-for="item in createMenuItems"
              :key="item.path"
              :to="item.path"
              class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-blue-50 hover:text-blue-700"
              :class="{ 'bg-blue-50 font-medium text-blue-700': isActive(item.path) }"
              @click="createMenuOpen = false"
            >
              {{ item.meta.navLabel }}
            </RouterLink>
          </div>
        </div>

        <!-- Other Nav Items (non-Dashboard) -->
        <template v-for="routeItem in navRoutes" :key="routeItem.path">
          <RouterLink
            v-if="routeItem.meta.navLabel !== 'Dashboard' && !routeItem.meta.createGroup"
            :to="routeItem.path"
            class="uppercase transition-colors hover:text-blue-600"
            :class="{ 'font-semibold text-blue-600': isActive(routeItem.path) }"
          >
            {{ routeItem.meta.navLabel }}
          </RouterLink>
        </template>

        <!-- More Dropdown (Grouped) -->
        <div
          v-if="moreMenuRoutes.length"
          ref="moreMenuContainer"
          class="relative"
          @keydown.esc="closeMoreMenu"
        >
          <button
            ref="moreMenuButton"
            type="button"
            :aria-expanded="moreMenuOpen"
            aria-controls="more-menu"
            @click="toggleMoreMenu"
            class="uppercase transition-colors hover:text-blue-600"
            :class="{ 'font-semibold text-blue-600': moreMenuOpen }"
          >
            MORE
          </button>
          <div
            v-if="moreMenuOpen"
            id="more-menu"
            class="absolute right-0 z-dropdown mt-2 max-h-[calc(100dvh-8rem)] w-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-2 shadow-lg"
          >
            <template v-for="(group, index) in groupedMoreMenuRoutes" :key="group.name">
              <div v-if="index > 0" class="mx-3 my-2 border-t border-gray-100"></div>

              <div
                class="px-4 py-1 text-[10px] font-bold uppercase tracking-wider"
                :class="group.name === 'Quick Access' ? 'text-brand-blue' : 'text-gray-400'"
              >
                {{ group.name }}
              </div>

              <router-link
                v-for="routeItem in group.routes"
                :key="routeItem.path"
                :to="routeItem.path"
                class="block px-4 py-2 transition hover:bg-blue-50 hover:text-blue-700"
                :class="
                  group.name === 'Quick Access'
                    ? 'bg-blue-50/40 text-sm font-semibold text-gray-900'
                    : 'text-sm text-gray-700'
                "
                @click="moreMenuOpen = false"
              >
                {{ routeItem.meta.navLabel }}
              </router-link>
            </template>
          </div>
        </div>
      </nav>

      <div
        class="col-start-3 row-start-1 flex items-center gap-1 text-gray-600 sm:gap-4 xl:col-start-4"
      >
        <AlertsIndicator />

        <!-- User Profile Dropdown -->
        <div ref="userMenuContainer" class="relative">
          <div class="flex items-center gap-2">
            <div class="relative">
              <button
                type="button"
                aria-label="User menu"
                :aria-expanded="userMenuOpen"
                aria-controls="user-menu"
                @click="toggleUserMenu"
                class="flex items-center transition-colors hover:text-blue-600"
                :class="{ 'font-semibold text-blue-600': userMenuOpen }"
              >
                <UserIcon class="h-7 w-7" />
              </button>
            </div>
          </div>

          <div
            v-if="userMenuOpen"
            id="user-menu"
            class="absolute right-0 z-dropdown mt-2 max-h-[calc(100dvh-5rem)] w-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          >
            <div class="border-b border-gray-200 px-4 py-3">
              <div class="text-sm font-semibold text-gray-800">{{ currentUserDisplayName }}</div>
              <div class="text-xs text-gray-500">{{ currentUserRole }}</div>
            </div>

            <div v-if="userStore.user">
              <RouterLink
                :to="{ name: 'UserHome' }"
                @click="userMenuOpen = false"
                class="flex w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                <UserCircle class="icon-xs text-brand-blue" />
                My Page
              </RouterLink>
              <RouterLink
                v-if="flagsStore.isModuleEnabled('expenses')"
                :to="{ name: 'ExpenseReports' }"
                @click="userMenuOpen = false"
                class="flex w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                <Receipt class="icon-xs text-brand-blue" />
                Expense Reports
              </RouterLink>
              <RouterLink
                v-if="flagsStore.isModuleEnabled('bugReports')"
                :to="{ name: 'BugReports' }"
                @click="userMenuOpen = false"
                class="flex w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                <Bug class="icon-xs text-brand-blue" />
                Report a Bug
              </RouterLink>
              <button
                v-if="userStore.isStaff && !userStore.isImpersonating"
                @click="openChangePinModal"
                class="flex w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                <KeyRound class="icon-xs text-brand-blue" />
                Change your PIN
              </button>
              <button
                v-if="userStore.isSuperAdmin && !userStore.isImpersonating"
                @click="openImpersonationModal"
                class="block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Impersonate User...
              </button>
              <button
                @click="forceTokenRefresh"
                class="block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Refresh Permissions
              </button>
              <div
                @click="logout"
                role="button"
                tabindex="0"
                class="block w-full cursor-pointer px-4 py-2 text-left text-sm text-red-600 hover:bg-red-100"
              >
                Logout
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
  <ImpersonationModal v-if="isImpersonationModalOpen" @close="isImpersonationModalOpen = false" />
  <ChangePinModal v-if="isChangePinModalOpen" @close="isChangePinModalOpen = false" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, markRaw } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getAuth } from 'firebase/auth'
import { useUserStore } from '@/stores/userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import {
  User as UserIcon,
  UserPlus,
  Play,
  Archive,
  Bug,
  Receipt,
  UserCircle,
  Smartphone,
  LayoutDashboard,
  KeyRound,
} from 'lucide-vue-next'
import { routes } from '@/router'
import { useToast } from '@/composables/useToast'
import { useDeviceDetect, isMobileDevice } from '@/composables/useDeviceDetect'
import CustomerSearchAutocomplete from '@/components/Common/CustomerSearchAutocomplete.vue'
import ImpersonationModal from '@/components/Admin/ImpersonationModal.vue'
import ChangePinModal from '@/components/Layout/ChangePinModal.vue'
import AlertsIndicator from '@/components/Layout/AlertsIndicator.vue'

const auth = getAuth()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const permStore = usePermissionsStore()
const flagsStore = useFrontendFlagsStore()
const toast = useToast()
const { returnToMobile } = useDeviceDetect()

// Ever-present "Back to mobile" affordance for a phone user who's on the desktop
// site (they escaped via "Full site"). Clears the force-desktop override + returns.
const showBackToMobile = computed(() => isMobileDevice() && !route.path.startsWith('/m'))
function backToMobile() {
  returnToMobile()
  router.push('/m')
}

// All possible dashboard sub-items with their route metadata
const allDashboardItems = [
  {
    label: 'Command Board',
    path: '/command-board',
    icon: markRaw(LayoutDashboard),
    resourceKey: 'CommandBoard',
    module: 'commandBoard_enabled',
  },
  {
    label: 'Leads',
    path: '/leads',
    icon: markRaw(UserPlus),
    resourceKey: 'LeadDashboard',
    module: 'leadManagement',
  },
  { label: 'Active', path: '/dashboard', icon: markRaw(Play), resourceKey: 'Dashboard' },
  { label: 'Finished', path: '/archive', icon: markRaw(Archive), resourceKey: 'ArchiveBrowser' },
]

// Dynamically filter dashboard dropdown based on access control matrix
const dashboardMenuItems = computed(() => {
  const userRoles = userStore.roles
  const isSuperAdmin = userStore.isSuperAdmin

  return allDashboardItems.filter(item => {
    if (item.module && !flagsStore.isModuleEnabled(item.module)) return false
    return permStore.canAccessPage(item.resourceKey, userRoles, isSuperAdmin)
  })
})

const userMenuOpen = ref(false)
const userMenuContainer = ref(null)
const moreMenuOpen = ref(false)
const moreMenuContainer = ref(null)
const moreMenuButton = ref(null)
const dashboardMenuOpen = ref(false)
const dashboardMenuContainer = ref(null)
const dashboardMenuButton = ref(null)
const createMenuOpen = ref(false)
const createMenuContainer = ref(null)
const createMenuButton = ref(null)
const isImpersonationModalOpen = ref(false)
const isChangePinModalOpen = ref(false)

// Check if current route is a dashboard route
const isActiveDashboardRoute = computed(() => {
  const dashboardPaths = allDashboardItems.map(item => item.path)
  return dashboardPaths.includes(route.path)
})

// Prettify the roles for the dropdown display
const currentUserRole = computed(() => {
  const roles = userStore.roles
  if (!roles.length) return 'No Role'

  return roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')
})

// Filter routes based on permissions
const navRoutes = computed(() => {
  const userRoles = userStore.roles
  const isSuperAdmin = userStore.isSuperAdmin

  return routes.filter(r => {
    const meta = r.meta || {}

    if (!meta.navLabel || meta.inMoreMenu || meta.dashboardGroup) return false

    // Module flag check — hide routes for disabled modules
    if (meta.module && !flagsStore.isModuleEnabled(meta.module)) return false

    // SuperAdmin-only routes: only visible to superAdmins
    if (meta.superAdminOnly) return isSuperAdmin

    // Solar Studio (create surface): per-user permission override (#34 — replaces the Create page)
    if (r.name === 'SolarStudio') {
      return (
        userStore.canCreateCustomers &&
        permStore.canAccessPage(meta.resourceKey, userRoles, userStore.isSuperAdmin)
      )
    }

    // Standard: use the access control matrix
    if (meta.resourceKey) {
      return permStore.canAccessPage(meta.resourceKey, userRoles, userStore.isSuperAdmin)
    }

    // No resourceKey (shouldn't happen after Phase 3, but safe fallback)
    return true
  })
})

const moreMenuRoutes = computed(() => {
  const userRoles = userStore.roles
  const isSuperAdmin = userStore.isSuperAdmin

  return routes.filter(r => {
    const meta = r.meta || {}

    // Must be a "More" menu item
    if (!meta.navLabel || !meta.inMoreMenu) return false

    // Module flag check — hide routes for disabled modules
    if (meta.module && !flagsStore.isModuleEnabled(meta.module)) return false

    // SuperAdmin-only routes: only visible to superAdmins
    if (meta.superAdminOnly) return isSuperAdmin

    // Standard: use the access control matrix
    if (meta.resourceKey) {
      return permStore.canAccessPage(meta.resourceKey, userRoles, userStore.isSuperAdmin)
    }

    // No resourceKey fallback
    return true
  })
})

const createMenuItems = computed(() => navRoutes.value.filter(item => item.meta.createGroup))
function closeCreateMenu() {
  createMenuOpen.value = false
  createMenuButton.value?.focus()
}

const groupedMoreMenuRoutes = computed(() => {
  const groups = {}
  const order = [
    'Quick Access',
    'Operations',
    'Supply Chain',
    'People',
    'Outreach',
    'Finance',
    'Pools',
    'Admin',
  ]

  moreMenuRoutes.value.forEach(route => {
    const cat = route.meta.category || 'General'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(route)
  })

  const result = order
    .map(name => {
      if (groups[name]) {
        const r = { name, routes: groups[name] }
        delete groups[name]
        return r
      }
      return null
    })
    .filter(Boolean)

  Object.keys(groups).forEach(name => {
    result.push({ name, routes: groups[name] })
  })

  return result
})

const currentUserDisplayName = computed(() => userStore.user?.displayName || 'Unnamed User')

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const toggleUserMenu = () => (userMenuOpen.value = !userMenuOpen.value)
const toggleMoreMenu = () => (moreMenuOpen.value = !moreMenuOpen.value)
const toggleDashboardMenu = () => (dashboardMenuOpen.value = !dashboardMenuOpen.value)
const toggleCreateMenu = () => (createMenuOpen.value = !createMenuOpen.value)

function closeDashboardMenu() {
  dashboardMenuOpen.value = false
  dashboardMenuButton.value?.focus()
}

function closeMoreMenu() {
  moreMenuOpen.value = false
  moreMenuButton.value?.focus()
}

const handleClickOutside = event => {
  if (userMenuContainer.value && !userMenuContainer.value.contains(event.target))
    userMenuOpen.value = false
  if (moreMenuContainer.value && !moreMenuContainer.value.contains(event.target))
    moreMenuOpen.value = false
  if (dashboardMenuContainer.value && !dashboardMenuContainer.value.contains(event.target))
    dashboardMenuOpen.value = false
  if (createMenuContainer.value && !createMenuContainer.value.contains(event.target))
    createMenuOpen.value = false
}

const openImpersonationModal = () => {
  isImpersonationModalOpen.value = true
  userMenuOpen.value = false
}
// The signed-in person's PIN for the AI phone line (docs/features/ai_phone_personal_pins_FEATURE.md).
const openChangePinModal = () => {
  isChangePinModalOpen.value = true
  userMenuOpen.value = false
}

const isActive = path => route.path === path

const logout = async () => {
  try {
    userMenuOpen.value = false
    moreMenuOpen.value = false
    dashboardMenuOpen.value = false
    createMenuOpen.value = false

    await userStore.signOutUser()
    toast.success('Signed out successfully.')
  } catch (err) {
    console.error('Logout error:', err)
    toast.error('Failed to sign out. Please try again.')
  }
}

async function forceTokenRefresh() {
  const user = auth.currentUser
  if (!user) {
    toast.warning('No user is currently signed in.')
    return
  }

  try {
    toast.info('Refreshing user permissions...')

    const tokenResult = await user.getIdTokenResult(true)

    if (typeof userStore.setSuperAdmin === 'function') {
      userStore.setSuperAdmin(!!tokenResult.claims.isSuperAdmin)
    }

    if (typeof userStore.setRoles === 'function') {
      const claimRoles = tokenResult.claims.userRole || tokenResult.claims.roles
      if (claimRoles) userStore.setRoles(claimRoles)
    }

    toast.success('Permissions refreshed!')

    setTimeout(() => window.location.reload(), 800)
  } catch (error) {
    toast.error('Failed to refresh permissions.')
    console.error('Token refresh error:', error)
  }
}
</script>
